<template>
  <div class="donation-background" :style="backgroundCSS">
    <div class="donation-wrapper">
      <div class="member-form__head" :style="headCSS">
        <div
          :class="{ 'member-form__title-nobg': !background }"
          class="member-form__title"
        >
          <h1>{{ title }}</h1>
        </div>

        <div v-if="subtitle && subtitle !== ''" class="member-form__subtitle">
          <h2>{{ modalHeader }}</h2>
        </div>

        <div class="member-form__body" v-html="body" />
      </div>

      <div class="member-form">
        <BankAccount
          v-if="bankAccountModal"
          @close="closeBankAccountModal"
          :label="labels.selectBankAccount"
          :submit="labels.submitBankAccount"
          @select="selectBankAccountFromModal"
          :accounts="bankAccountRetrieval.accounts"
          :header="labels.gettingAccounts"
          :qr="qr"
          :showQr="showQr"
          :qrMessage="labels.qrBankText"
          :qrMessageMobile="labels.qrBankTextMobile"
          :autoStartToken="autoStartToken"
          :sameDevice="labels.sameDeviceBank"
          :anotherDevice="labels.anotherDeviceBank"
          :sameDeviceMobile="labels.sameDeviceBankMobile"
          :anotherDeviceMobile="labels.anotherDeviceBankMobile"
          :bankAccountRetrieval="bankAccountRetrieval"
          :text="bankIdText"
          :modalHeader="bankAccountHeader"
          :modalText="bankAccountText"
          @proceedSameDevice="sameDeviceQr"
          @proceedAnotherDevice="anotherDeviceQr"
        />

        <div class="member-form__fields member-form__amounts">
          <div class="member-form__label">
            <label>{{ labels.amounts_label }}</label>
          </div>
          <template v-if="amounts.length">
            <div
              v-for="amount in amounts"
              :key="amount"
              @change="prefilledAmountChanged"
              class="member-form__form-control member-form__fields--field"
            >
              <input
                :id="makeId('amount-', amount)"
                v-model="prefilled"
                :value="amount"
                type="radio"
              />
              <label :for="makeId('amount-', amount)">{{
                amount | currency(showCurrency, currency)
              }}</label>
            </div>
          </template>
        </div>

        <div v-if="gdpr !== ''" v-html="gdpr" class="member-form__gdpr" />

        <div class="member-form__fields member-form__mailandtel">
          <div class="member-form__label">
            <label>{{ labels.contact }}</label>
          </div>
          <div
            class="member-form__form-control member-form__fields--field"
            :class="{
              'member-form__form-control-valid-parent':
                validate && isValid('email'),
              'member-form__form-control-error-parent':
                validate && !isValid('email'),
            }"
          >
            <input
              v-model="form.email"
              :placeholder="labels.email"
              :class="{
                'member-form__form-control-valid': validate && isValid('email'),
                'member-form__form-control-error':
                  validate && !isValid('email'),
              }"
              type="email"
              name="email"
            />
          </div>
          <div
            class="member-form__form-control member-form__fields--field"
            :class="{
              'member-form__form-control-valid-parent':
                validate && isValid('phone'),
              'member-form__form-control-error-parent':
                validate && !isValid('phone'),
            }"
          >
            <input
              v-model="form.phone"
              :placeholder="labels.phone"
              :class="{
                'member-form__form-control-valid': validate && isValid('phone'),
                'member-form__form-control-error':
                  validate && !isValid('phone'),
              }"
              type="phone"
              name="phone"
            />
          </div>
        </div>

        <div
          v-if="paymentMethods.length > 1"
          class="member-form__fields member-form__methods"
        >
          <div class="member-form__label">
            <label>{{ labels.payment_label }}</label>
          </div>
          <div
            v-for="method in paymentMethods"
            :key="method"
            :class="{ 'member-form__fields-pristine': form.method === null }"
            class="member-form__fields--field"
          >
            <input
              :id="makeId('method-', method)"
              v-model="form.method"
              type="radio"
              :value="method"
            />
            <label
              :for="makeId('method-', method)"
              :class="makeClass(method)"
              >{{ labels[method] }}</label
            >
          </div>
        </div>

        <div id="errors" v-if="errorPositionTop === true">
          <div class="member-form__error" v-html="error" />
          <div class="member-form__success" v-html="message" />
        </div>

        <div class="member-form__address-wrapper">
          <div class="member-form__label">
            <label>{{ labels.contact }}</label>
          </div>
          <div
            v-if="getAddressFromSocialSecurity === true"
            class="member-form__fields member-form__getaddress"
          >
            <div
              class="member-form__form-control member-form__fields--field"
              :class="{
                'member-form__form-control-valid-parent':
                  validate && isValid('socialSecurityNumber'),
                'member-form__form-control-error-parent':
                  validate && !isValid('socialSecurityNumber'),
              }"
            >
              <input
                v-model="form.socialSecurityNumber"
                :placeholder="labels.socialSecurityNumber"
                :class="{
                  'member-form__form-control-valid':
                    validate && isValid('socialSecurityNumber'),
                  'member-form__form-control-error':
                    validate && !isValid('socialSecurityNumber'),
                }"
                type="number"
                name="socialsecuritynumber"
              />
            </div>
            <div class="member-form__form-control member-form__fields--field">
              <button
                @click.prevent="getOrEmptyAddress"
                :class="{
                  'member-form__button-loading':
                    addressRetrievalInProgress === true,
                }"
                id="bank-button"
              >
                {{ addressLabel }}
              </button>
            </div>
          </div>

          <div class="member-form__fields member-form__names">
            <div class="member-form__form-control member-form__fields--field">
              <maskable-input
                v-model="form.firstName"
                :placeholder="labels.firstName"
                :class="{
                  'member-form__form-control-valid':
                    validate && isValid('firstName'),
                  'member-form__form-control-error':
                    validate && !isValid('firstName'),
                }"
                :masked="hideAddress && maskAddress"
                name="firstname"
              />
            </div>
            <div class="member-form__form-control member-form__fields--field">
              <maskable-input
                v-model="form.lastName"
                :placeholder="labels.lastName"
                :class="{
                  'member-form__form-control-valid':
                    validate && isValid('lastName'),
                  'member-form__form-control-error':
                    validate && !isValid('lastName'),
                }"
                :masked="hideAddress && maskAddress"
                name="lastname"
              />
            </div>
          </div>
          <div
            v-if="isCompany"
            class="member-form__fields member-form__company"
          >
            <div class="member-form__form-control member-form__fields--field">
              <input
                v-model="form.company"
                :placeholder="labels.company"
                name="company"
              />
            </div>
            <div class="member-form__form-control member-form__fields--field">
              <input
                v-model="form.department"
                :placeholder="labels.department"
                name="department"
              />
            </div>
          </div>
          <div class="member-form__fields member-form__address">
            <div class="member-form__form-control member-form__fields--field">
              <maskable-input
                v-model="form.address"
                :placeholder="labels.address"
                :class="{
                  'member-form__form-control-valid':
                    validate && isValid('address'),
                  'member-form__form-control-error':
                    validate && !isValid('address'),
                }"
                :masked="hideAddress && maskAddress"
                name="address"
              />
            </div>
            <div class="member-form__form-control member-form__fields--field">
              <maskable-input
                v-model="form.address2"
                :placeholder="labels.address2"
                :masked="hideAddress && maskAddress"
                name="co"
              />
            </div>
          </div>
          <div class="member-form__fields member-form__city">
            <div class="member-form__form-control member-form__fields--field">
              <maskable-input
                v-model="form.zip"
                :placeholder="labels.zip"
                :class="{
                  'member-form__form-control-valid': validate && isValid('zip'),
                  'member-form__form-control-error':
                    validate && !isValid('zip'),
                }"
                :masked="hideAddress && maskAddress"
                name="zip"
              />
            </div>
            <div class="member-form__form-control member-form__fields--field">
              <maskable-input
                v-model="form.city"
                :placeholder="labels.city"
                :class="{
                  'member-form__form-control-valid':
                    validate && isValid('city'),
                  'member-form__form-control-error':
                    validate && !isValid('city'),
                }"
                :masked="hideAddress && maskAddress"
                name="city"
              />
            </div>
          </div>

          <div
            v-if="isAutogiro && banks.length > 0"
            class="member-form__fields member-form__bank1"
          >
            <div class="member-form__form-control member-form__fields--field">
              <select
                v-model="form.bank"
                :class="{
                  'member-form__form-control-valid':
                    validate && form.bank !== 0,
                  'member-form__form-control-error':
                    validate && form.bank === 0,
                }"
                name="bank"
              >
                <option value="0" disabled="disabled" selected>
                  {{ labels.chooseBank }}
                </option>
                <option v-for="bank in banks" :key="bank.id" :value="bank.id">
                  {{ bank.name }}
                </option>
              </select>
            </div>
            <div class="member-form__form-control member-form__fields--field">
              <input
                v-model="form.bankClearing"
                :placeholder="labels.bankClearing"
                :class="{
                  'member-form__form-control-valid':
                    validate && isValid('bankClearing'),
                  'member-form__form-control-error':
                    validate && !isValid('bankClearing'),
                }"
                type="tel"
                name="clearing"
              />
            </div>
          </div>
          <div
            v-if="isAutogiro && banks.length > 0"
            class="member-form__fields member-form__bank2"
          >
            <div class="member-form__form-control member-form__fields--field">
              <input
                v-model="form.bankAccount"
                :placeholder="labels.bankAccount"
                :class="{
                  'member-form__form-control-valid':
                    validate && isValid('bankAccount'),
                  'member-form__form-control-error':
                    validate && !isValid('bankAccount'),
                }"
                type="tel"
                name="account"
              />
            </div>
            <div class="member-form__form-control member-form__fields--field">
              <button
                @click.prevent="openBankAccountModal"
                :disabled="bankAccountRetrieval.status !== false"
                :class="{
                  'member-form__button-loading':
                    bankAccountRetrieval.status !== false,
                }"
              >
                {{ labels.getBankAccount }}
              </button>
            </div>
          </div>

          <div
            v-if="checkboxText && checkboxText !== ''"
            class="member-form__checkbox"
          >
            <input
              :class="{
                'member-form__form-control-error':
                  validate && checked === false,
              }"
              type="checkbox"
              v-model="checked"
            />
            <span>{{ checkboxText }}</span>
          </div>

          <div v-if="terms !== ''" class="member-form__terms">
            <div
              v-html="showTerms ? terms : $options.filters.preview(terms, 75)"
              class="member-form__terms-preview"
            />
            <div
              v-if="$options.filters.stripTags(terms).length > 75"
              class="member-form__terms-toggle"
            >
              <a @click.prevent="toggleTerms" href="#">{{
                showTerms ? labels.showLess : labels.showMore
              }}</a>
            </div>
          </div>
        </div>

        <div id="errors" v-if="errorPositionTop === false">
          <div class="member-form__error" v-html="error" />
          <div class="member-form__success" v-html="message" />
        </div>

        <BankId
          v-if="bankIdModal"
          @close="closeBankIdModal"
          :text="labels.qrBankId"
          :header="bankIdHeader"
          :paymentButtonLoading="paymentButtonLoading"
          :sameDevice="labels.sameDevice"
          :anotherDevice="labels.anotherDevice"
          :message="bankIdSignature.message"
          :showMessage="showBankIdMessage"
          :qr="qr"
          @proceedSameDevice="autoStart"
        />

        <div v-else class="member-form__form-control member-form__submit">
          <button
            @click.prevent="submit"
            :disabled="submitDisabled"
            :class="{
              'member-form__button-loading': paymentButtonLoading === true,
            }"
          >
            {{ labels.submit }}
          </button>
        </div>

        <div
          id="stripe-wrapper"
          v-if="hasStripe"
          v-show="showStripe"
          class="member-form__stripe"
        >
          <Stripe
            @completed="finishStripe"
            @initiated="initiateStripe"
            :pk="stripeKey"
            :labels="labels"
            :amount="form.amount"
            :name="fullName"
          />
        </div>

        <Swish
          v-if="hasSwish"
          v-show="showSwish"
          @close="closeSwish"
          class="member-form__swish"
          :lead-id="form.leadId"
          :amount="form.amount"
          :labels="labels"
          :name="fullName"
          :phone="form.phone"
          :payment-id="paymentId"
        />
      </div>
    </div>
  </div>
</template>

<script>
import 'idempotent-babel-polyfill';

import MaskableInput from './MaskableInput';
import donationMix from './mixin.js';
import Stripe from './Stripe';
import Swish from './Swish';
import BankId from './BankId';
import BankAccount from './BankAccount';

export default {
  mixins: [donationMix],

  name: 'Member',

  components: {
    MaskableInput,
    Swish,
    BankId,
    BankAccount,
    Stripe,
  },

  props: {
    paymentMethods: {
      type: Array,
      default: () => [],
    },
    getBankAccountFromSocialSecurity: {
      type: Boolean,
      default: true,
    },
    banks: {
      type: Array,
      default: () => [],
    },
    termsSimple: {
      type: String,
      default: null,
    },
    termsRecurrent: {
      type: String,
      default: null,
    },
    confirmationSimple: {
      type: String,
      default: null,
    },
    confirmationRecurrent: {
      type: String,
      default: null,
    },
    checkboxText: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      validate: false,
      scrolled: false,
      checked: false,
      form: {
        template: 'membership',
      },
    };
  },

  watch: {
    showStripe: function (value) {
      if (value) {
        setTimeout(() =>
          this.$scrollTo('#stripe-wrapper', 500, { offset: -370 }),
        );
      }
    },
  },

  mounted() {
    this.formName = this.title;
  },

  computed: {
    validationRegExpr() {
      return { ...this.defaultValidation, ...this.validation };
    },

    isAutogiro() {
      return (
        this.form.method &&
        /(^|.*-)autogiro/.test(this.form.method.split('|')[0])
      );
    },

    backgroundCSS() {
      return this.background && this.imagePosition == 0
        ? "background-image: url('" + this.background + "')"
        : null;
    },

    headCSS() {
      return this.background && this.imagePosition == 1
        ? "background-image: url('" + this.background + "')"
        : null;
    },

    returnUrl() {
      return this.confirmation;
    },

    paymentButtonLoading() {
      return (
        this.payexRedirectInProgress || this.bankIdSignature.status !== false
      );
    },

    showAccountModal() {
      return (
        this.isAutogiro &&
        this.banks.length > 0 &&
        this.bankAccountRetrieval.accounts.length &&
        this.bankAccountRetrieval.selected === false
      );
    },

    confirmation() {
      return this.isAutogiro === true
        ? this.confirmationRecurrent
        : this.confirmationSimple;
    },

    terms() {
      return this.isAutogiro === true ? this.termsRecurrent : this.termsSimple;
    },

    hasStripe() {
      return this.paymentMethods.indexOf('stripe') > -1;
    },

    hasSwish() {
      return this.paymentMethods.indexOf('swish-direct') > -1;
    },
  },

  methods: {
    submit() {
      this.validate = true;
      this.form.amount = this.prefilled
        ? this.prefilled
        : parseInt(this.amount);
      if (this.form.amount === null || isNaN(this.form.amount)) {
        this.error = this.labels.errorNoAmount;
        return false;
      }
      if (this.form.amount < this.min) {
        this.error = this.labels.errorMinimum.replace(/_AMOUNT_/, this.min);
        return false;
      }
      if (this.form.method === null && this.paymentMethods.length === 1) {
        this.form.method = this.paymentMethods[0];
      }
      if (this.form.method === null) {
        this.error = this.labels.errorNoPaymentMethod;
        return false;
      } else if (this.checked === false) {
        this.error = this.labels.errorCheckbox;
        return false;
      } else if (!this.isValid('socialSecurityNumber')) {
        this.error = this.labels.errorSSN;
        return false;
      } else if (
        this.form.method === 'swish' ||
        this.form.method === 'creditcard' ||
        this.form.method === 'directdebit'
      ) {
        this.message = this.labels.redirectPayex;
        return this.payex();
      } else if (this.form.method === 'stripe') {
        this.showStripe = true;
        return this.saveLead();
      } else if (this.form.method === 'swish-direct') {
        this.swish();
      } else if (this.isAutogiro) {
        return this.startBankIdSign(true);
      } else {
        this.error = this.labels.error;
      }
    },

    closeModal() {
      this.qr = null;
      this.bankAccountRetrieval.accounts = [];
    },

    selectBankAccountFromModal(account) {
      this.qr = null;
      this.form.bankAccount = account.AccountNo;
      this.form.bankClearing = account.ClearingNo;
      this.bankAccountRetrieval.accounts = [];
      this.bankAccountRetrieval.selected = true;
    },

    prefilledAmountChanged() {
      this.form.amount = this.prefilled;
      this.amount = null;
    },

    makeId(base, id) {
      return base + id;
    },

    makeClass(method) {
      return 'member-form__method-' + method;
    },
  },
};
</script>

<style lang="scss">
.member-form {
  text-align: center;
  margin: auto;
  max-width: 800px;

  input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &__error {
    color: red;
  }

  &__success {
    color: green;
  }

  &__form-control {
    margin: 10px;

    input,
    button {
      padding: 10px;
      width: 300px;
    }

    &-valid {
      border: 1px solid green;
    }

    &-error {
      border: 1px solid red;
    }
  }

  &__terms-toggle {
    font-size: 75%;
    text-decoration: underline;
  }

  &__fields {
    display: flex;
    justify-content: center;
    margin: 20px;
    &--field {
      flex: 0 0 auto;
      input[type='radio'] {
        position: absolute;
        left: -9999px;
      }
      label {
        padding: 10px;
        cursor: pointer;
        color: black;
      }
      input[type='radio']:checked + label {
        background: green;
        color: white;
      }
    }
  }
  &__qr {
    margin: 20px auto;
    &-message {
      padding: 10px;
    }
    img {
      width: 150px;
      border: 1px solid #a5a5a5;
    }
  }
}

.donation-modal {
  position: fixed;
  z-index: 100;
  left: 0;
  top: 0;
  width: 100%;
  overflow: auto;
  background-color: #fff;
  background-color: rgba(255, 255, 255, 0.65);
  &__content {
    background-color: #fefefe;
    margin: 15% auto;
    border: 1px solid #888;
    position: fixed;
    height: 100%;
    bottom: 0;
    right: 0;
    left: 0;
    top: 0;
  }
  &__close {
    color: #aaa;
    float: right;
    font-size: 28px;
    font-weight: bold;
    &:hover,
    &:focus {
      color: black;
      text-decoration: none;
      cursor: pointer;
    }
  }
}
</style>
